import React from "react";
import _ from "lodash";
import styled from "styled-components";

import theme from "../../styles/theme";
import SeasonTable from "./SeasonTable";
import { useStandingsContext } from "../../contexts/standings";
import { useScheduleContext } from "../../contexts/schedule";
import { getDivisionColor, getDivisionPriority } from "../../utils/helpers/divisionUtils";
// Same badge the race results use, so a division looks identical in both views
import { DivisionBadge } from "../RaceResults";

const Container = styled.div`
  padding: 4px 0px;
  display: flex;
  flex-direction: column;
`;

const BadgeRow = styled.div`
  margin-top: 16px;
  margin-bottom: 12px;
`;

const Message = styled.div`
  padding: 32px 12px;
  color: ${theme.colors.grey};
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  text-align: center;
`;

const SkeletonLine = styled.div`
  height: 44px;
  border-radius: 4px;
  background-color: ${theme.colors.darkLight};
  opacity: ${(props) => props.$opacity};
  margin-bottom: 4px;
`;

/**
 * Division stacking for the Results page's standings view.
 *
 * Deliberately separate from DriverStandings even though the stacking is similar: that component
 * feeds the home page's championship table, and this one must not be able to break it.
 */
const StandingsTables = ({ seasonId, group = "drivers" }) => {
  const { standings, standingsStatus } = useStandingsContext();
  const { seasons } = useScheduleContext();

  const seasonStandings = standings[seasonId];
  const status = standingsStatus[seasonId];

  if (status === "error") {
    return <Message>Не вдалося завантажити залік. Спробуйте пізніше.</Message>;
  }

  if (!seasonStandings) {
    return (
      <Container>
        {_.times(10, (i) => (
          <SkeletonLine key={i} $opacity={1 - i * 0.08} />
        ))}
      </Container>
    );
  }

  const groupMap = seasonStandings[group];
  const divisionColors = seasons[seasonId]?.divisionColors;

  const divisions = Object.keys(groupMap || {})
    .filter((division) => (groupMap[division] || []).length > 0)
    .sort(
      (a, b) =>
        getDivisionPriority(divisionColors, a) - getDivisionPriority(divisionColors, b),
    );

  if (divisions.length === 0) {
    return <Message>Залік ще не сформовано</Message>;
  }

  const isMultiDivision = divisions.length > 1;

  return (
    <Container style={{ gap: isMultiDivision ? "24px" : "8px" }}>
      {divisions.map((division) => {
        const list = groupMap[division] || [];
        const divisionColor = getDivisionColor(divisionColors, division);

        return (
          <div key={division} style={{ display: "flex", flexDirection: "column" }}>
            {isMultiDivision && (
              <BadgeRow>
                <DivisionBadge name={division} color={divisionColor} />
              </BadgeRow>
            )}
            <SeasonTable
              seasonId={seasonId}
              mode={group === "teams" ? "teams" : "drivers"}
              drivers={group === "teams" ? undefined : list}
              teams={group === "teams" ? list : undefined}
              showHeader
              scrollable
            />
          </div>
        );
      })}
    </Container>
  );
};

export default StandingsTables;
